/**
 * API Configuration Provider — Canonical Auth Pattern
 *
 * This is the standard approach for configuring authenticated API clients
 * in the console app. All domain packages should use this pluggable fetch
 * pattern (not Axios interceptors) to attach Clerk session tokens and
 * organization/tenant context headers to outgoing requests.
 *
 * Pattern:
 *   1. Obtain token via useAuth().getToken()
 *   2. Attach `Authorization: Bearer <token>` header
 *   3. Attach `X-Tenant-ID: <orgId>` header when organization is active
 *   4. Pass the authenticated fetch to each domain package's configure* fn
 *
 * Must be rendered inside ClerkProvider.
 */
import { useEffect, type ReactNode } from 'react'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import {
  configureClaimsApi,
  configureBeneficiariesApi,
  configurePoliciesApi,
  configureAdminClaimsApi,
  configureAdminBeneficiariesApi,
  configureAdminPaymentsApi,
  configureAdminAccountsApi,
  configureProductsApi,
} from '@neutrino/ui-equinox-insurance'
import { getInsuranceServiceUrl } from '@/config/env.schema'

interface ApiConfigProviderProps {
  children: ReactNode
}

/**
 * Configures insurance API clients with authenticated fetch that
 * includes the Clerk session token and org context headers.
 * When VITE_INSURANCE_SERVICE_URL is set, relative URLs are
 * prefixed with the service origin for cross-origin requests.
 */
export function ApiConfigProvider({ children }: ApiConfigProviderProps) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  useEffect(() => {
    const insuranceBaseUrl = getInsuranceServiceUrl()

    const authenticatedFetch = async (
      url: string,
      options?: RequestInit,
    ): Promise<Response> => {
      const token = await getToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string>),
      }

      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
      if (organization?.id) {
        headers['X-Tenant-ID'] = organization.id
      }

      // Prepend insurance service URL for relative paths
      const resolvedUrl =
        insuranceBaseUrl && url.startsWith('/')
          ? `${insuranceBaseUrl}${url}`
          : url

      return fetch(resolvedUrl, { ...options, headers })
    }

    configureClaimsApi(authenticatedFetch)
    configureBeneficiariesApi(authenticatedFetch)
    configurePoliciesApi(authenticatedFetch)
    configureAdminClaimsApi(authenticatedFetch)
    configureAdminBeneficiariesApi(authenticatedFetch)
    configureAdminPaymentsApi(authenticatedFetch)
    configureAdminAccountsApi(authenticatedFetch)
    configureProductsApi(authenticatedFetch)
  }, [getToken, organization?.id])
  return <>{children}</>
}
